/**
 * ClinicReportSection — "דוחות קליניקה" tab of the unified Reports page.
 *
 * Reuses MonthlyReportContent for the meeting-analytics + growth widgets
 * (finance moved to the dedicated finance tab) and injects a 12-month
 * completed/cancelled trend BarChart built from the slim reports list.
 */

import { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import MonthlyReportContent from "../MonthlyReportContent";

// Chart series colors (recharts requires literal values; matches token palette)
const TREND_COMPLETED_COLOR = "#4dd9b4"; // --color-primary
const TREND_CANCELLED_COLOR = "#f87171"; // --color-error

const TREND_LABELS = {
  completed: "פגישות שהושלמו",
  cancelled: "פגישות שבוטלו",
};

const formatShortMonth = (year, month) =>
  new Date(Date.UTC(year, month - 1, 1, 12)).toLocaleDateString("he-IL", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });

const ClinicReportSection = ({ monthKey, reportsList, scopeTherapistId }) => {
  // 12-month completed/cancelled trend (list is newest-first).
  const trendData = useMemo(() => {
    if (!reportsList || reportsList.length === 0) return [];
    return reportsList
      .slice(0, 12)
      .slice()
      .reverse()
      .map((r) => {
        const src = scopeTherapistId
          ? r.perTherapistTotals?.[scopeTherapistId] || {}
          : r;
        return {
          name: formatShortMonth(r.year, r.month),
          completed: src.totalCompleted || 0,
          cancelled: src.totalCancelled || 0,
        };
      });
  }, [reportsList, scopeTherapistId]);

  const trendChart =
    trendData.length > 1 ? (
      <div className="mr-chart-card">
        <h3>מגמת פגישות — 12 חודשים אחרונים</h3>
        <div className="mr-chart-wrap">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={trendData}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: "#6b7280" }}
                tickLine={false}
                axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#6b7280" }}
                tickLine={false}
                axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value, name) => [value, TREND_LABELS[name] || name]}
                contentStyle={{
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 6,
                  fontSize: 12,
                  direction: "rtl",
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value) => TREND_LABELS[value] || value}
              />
              <Bar
                dataKey="completed"
                fill={TREND_COMPLETED_COLOR}
                radius={[4, 4, 0, 0]}
                barSize={16}
              />
              <Bar
                dataKey="cancelled"
                fill={TREND_CANCELLED_COLOR}
                radius={[4, 4, 0, 0]}
                barSize={16}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    ) : null;

  return (
    <MonthlyReportContent
      monthKey={monthKey}
      sections={["meetings", "growth"]}
      scopeTherapistId={scopeTherapistId ?? null}
      afterMeetingsChartsSlot={trendChart}
    />
  );
};

export default ClinicReportSection;
